import { useState } from "react";
import { generatePath, useOutletContext } from "react-router";
import { styled } from "@pigment-css/react";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import ActoRoutes from "@acto/taskpane/core/router/routes.config";
import {
  Card,
  KpiHeadline,
  MetaLabel,
  RowBody,
  RowChevron,
  RowItem,
  RowList,
  RowListEmpty,
  RowMeta,
  RowSkeletonList,
  RowTitle,
} from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { Divider } from "@acto/taskpane/field-sales-2/components/primitives/Divider";
import { Skeleton } from "@acto/taskpane/field-sales-2/components/primitives/Skeleton";
import { ChevronRightThin } from "@acto/web-common/components";
import { PageLayout } from "@acto/taskpane/field-sales-2/components/patterns/PageLayout";
import { YearFilterPill } from "@acto/taskpane/field-sales-2/components/patterns/YearFilterPill/YearFilterPill";
import { useClientOffers } from "@acto/taskpane/field-sales-2/data-access/useClientOffers";
import { AnalyticsEvents, useTrack, useViewTrackedOnce } from "@acto/taskpane/core/analytics";
import { formatNumericDayMonth } from "@acto/taskpane/field-sales-2/utils/format";
import { useNavigateWithReturn } from "@acto/taskpane/field-sales-2/navigation";
import type { CustomerOutletContext } from "@acto/taskpane/field-sales-2/CustomerDetailsLayout";
import { ShellSlotContent } from "@acto/taskpane/field-sales-2/shell";
import { useInfiniteScroll } from "./useInfiniteScroll";

const CURRENT_YEAR = new Date().getFullYear();

const SectionHeading = styled("h2")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[18],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: 1.32,
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

const TrendBadge = styled("div")(({ theme }) => ({
  flex: "0 0 auto",
  width: 56,
  height: 52,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: theme.fieldSalesTwo.radius[18],
  background: "rgba(19,19,19,0.04)",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[13],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: 1.32,
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.wide,
  color: theme.fieldSalesTwo.colors.neutral[800],
  flexShrink: 0,
}));

const OfferMeta = styled(RowMeta)(({ theme }) => ({
  fontSize: theme.fieldSalesTwo.font.size[14],
}));

const ScrollSentinel = styled("li")({ height: 1, listStyle: "none" });

export function OffersOverview() {
  const t = useTranslations("fieldSalesTwo");
  const { locale } = useLocale();
  const push = useNavigateWithReturn();

  const { customer, isPending: customerPending } = useOutletContext<CustomerOutletContext>();
  const track = useTrack();

  useViewTrackedOnce(() => {
    if (!customer) return;
    track(AnalyticsEvents.OFFERS_VIEWED, { customerId: customer.id, customerName: customer.name });
  }, customer?.id ?? null);

  // Years that have offers, newest first, straight from the API. Until the user picks, show the most
  // recent; a stale pick (after switching customers) falls back to it.
  const yearOptions = customer?.offerYears ?? [];
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const effectiveYear =
    selectedYear != null && yearOptions.includes(selectedYear) ? selectedYear : (yearOptions[0] ?? CURRENT_YEAR);
  const yearRange = { startDate: `${effectiveYear}-01-01`, endDate: `${effectiveYear}-12-31` };

  const { items, isPending, isEmpty, hasMore, isLoadingMore, loadMore, formattedTotalValue } = useClientOffers(
    customer?.id,
    yearRange,
  );

  const sentinelRef = useInfiniteScroll<HTMLLIElement>({ hasMore, isLoadingMore, loadMore, resetKey: items.length });

  const handleOpenOffer = (offerId: string) => {
    if (!customer) return;
    track(AnalyticsEvents.OFFER_OPENED, { offerId, customerId: customer.id, customerName: customer.name });
    push(generatePath(ActoRoutes.CUSTOMER_OFFER_DETAILS, { id: customer.id, offerId }));
  };

  const isOffersLoading = items.length === 0 && isPending;

  return (
    <PageLayout
      hPadding="card"
      isLoading={customerPending}
      isEmpty={!customer && !customerPending}
      emptyState={t("customerNotFound")}
    >
      <ShellSlotContent slot="topBarTrailing" id="offersYearFilter">
        {yearOptions.length > 1 ? (
          <YearFilterPill selectedYear={effectiveYear} yearOptions={yearOptions} onChange={setSelectedYear} />
        ) : null}
      </ShellSlotContent>
      <Card>
        <SectionHeading>{t("offersOverview.title")}</SectionHeading>
        <Divider />
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <MetaLabel>
            {t("offersOverview.totalValue")} {effectiveYear}
          </MetaLabel>
          {isPending ? <Skeleton.Line width="55%" height={40} /> : <KpiHeadline>{formattedTotalValue}</KpiHeadline>}
        </div>
      </Card>

      <Card style={isOffersLoading || isEmpty ? undefined : { paddingBottom: 0 }}>
        {isOffersLoading ? (
          <RowSkeletonList />
        ) : isEmpty ? (
          <RowListEmpty>{t("offersOverview.empty", { year: effectiveYear })}</RowListEmpty>
        ) : (
          <RowList role="list">
            {items.map((offer) => (
              <RowItem
                key={offer.id}
                onClick={() => handleOpenOffer(offer.id)}
                role="button"
                tabIndex={0}
                dividerInset
                style={{ cursor: "pointer" }}
              >
                <TrendBadge>
                  {offer.validFrom != null ? formatNumericDayMonth(offer.validFrom, locale) : "—"}
                </TrendBadge>
                <RowBody>
                  <RowTitle>{offer.externalReference}</RowTitle>
                  <OfferMeta>{offer.formattedValue}</OfferMeta>
                </RowBody>
                <RowChevron>
                  <ChevronRightThin strokeWidth={1.5} />
                </RowChevron>
              </RowItem>
            ))}
            <ScrollSentinel ref={sentinelRef} aria-hidden="true" />
          </RowList>
        )}
      </Card>
    </PageLayout>
  );
}
